const RECAPTCHA_SCRIPT_ID = 'recaptcha-api-v3';
const RECAPTCHA_ACTION = 'newsletter_signup';
const COALESCE_MS = 1500;

let scriptLoadPromise = null;
let lastTokenPromise = null;
let lastTokenTime = 0;

export function loadRecaptchaScript(siteKey) {
  if (!siteKey || typeof siteKey !== 'string') {
    return Promise.reject(new Error('Missing reCAPTCHA site key'));
  }

  if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.ready) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    if (document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      window.grecaptcha?.ready?.(resolve);
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(resolve);
      } else {
        resolve();
      }
    };
    const loadError = new Error('reCAPTCHA script failed to load');
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(loadError);
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export function getToken(siteKey) {
  if (!siteKey || typeof siteKey !== 'string') {
    return Promise.resolve(null);
  }

  const now = Date.now();
  if (lastTokenPromise && now - lastTokenTime < COALESCE_MS) {
    return lastTokenPromise;
  }

  lastTokenTime = now;
  lastTokenPromise = loadRecaptchaScript(siteKey)
    .then(() => {
      if (typeof window.grecaptcha?.execute !== 'function') {
        return null;
      }
      return window.grecaptcha.execute(siteKey, { action: RECAPTCHA_ACTION });
    })
    .then((token) => token || null)
    .catch((err) => {
      if (err?.message === 'reCAPTCHA script failed to load') {
        throw err;
      }
      return null;
    })
    .finally(() => {
      if (Date.now() - lastTokenTime >= COALESCE_MS) {
        lastTokenPromise = null;
      }
    });

  return lastTokenPromise;
}

export function isRecaptchaAvailable() {
  return typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.execute === 'function';
}
